import React from 'react';

import Button from 'components/common/Button';
import EditableCell, {
  EditableSelectOption,
} from 'components/common/EditableCell';
import NumericInputField from 'components/common/NumericInputField';
import ShortcutHint from 'components/common/ShortcutHint/ShortcutHint';
import TextAreaField from 'components/common/TextAreaField';
import {
  TASKS_GRID_COLUMN_LABEL_AREA,
  TASKS_GRID_COLUMN_LABEL_AREA_UNIT,
  TASKS_GRID_COLUMN_LABEL_DATE,
  TASKS_GRID_COLUMN_LABEL_TIMING,
  TASKS_GRID_NOTES_FIELD_LABEL,
  TASKS_GRID_NOTES_PLACEHOLDER,
  TASKS_GRID_SELECT_UNIT_PLACEHOLDER,
  TASKS_GRID_TARGET_FIELD_LABEL,
  TASKS_GRID_WATER_RATE_FIELD_LABEL,
  TASKS_GRID_WATER_RATE_PLACEHOLDER,
  TASKS_GRID_WATER_RATE_UNIT_LABEL,
  TASKS_SHEET_ADD_INPUTS_LABEL,
} from 'constants/uiConstants';
import { TASK_NOTES_MAX_LENGTH } from 'constants/validationConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInputTask, IInputTaskBase } from 'interfaces/IInputTask';

import { isOtherCostTask } from './otherCosts';
import { isApplicationLikeTask } from './stepCalculations';
import {
  TASK_DATE_CELL_ATTR,
  TASK_ENTRY_ADD_INPUTS_ATTR,
} from './taskEntryFocus';
import { ApplicationTaskFields, formatDate } from './taskFactory';
import TargetSpeciesPicker from './TargetSpeciesPicker';
import TimingCell from './TimingCell';

interface TaskSheetDetailsFormProps {
  task: IInputTask;
  gUnits: GUnit[];
  selectedInputStartDate: string | null;
  areaUnitOptions: EditableSelectOption[];
  commitTaskField: (
    task: IInputTask,
    patch: Partial<IInputTaskBase & ApplicationTaskFields>
  ) => void;
  /** Fires when the date picker closes, committed or not. */
  onDateEditEnd?: (taskId: string) => void;
  /**
   * Water rate, Target and Notes normally live on the grid itself; each
   * shows here only while View options hides it there, so it can still be
   * edited.
   */
  showWaterRate: boolean;
  showTarget: boolean;
  showNotes: boolean;
  /**
   * Set while the task is being entered (OP-2556): every field shows, so the
   * user can set the task up in one place, and an "Add inputs" button at the
   * foot moves on to the product picker. Nothing advances on its own — most
   * fields may stay empty — but picking the date puts focus on the button,
   * so Enter is the fast way on.
   */
  onAddInputs?: () => void;
}

// The editors draw the field's own border (grey at rest, green on hover and
// while editing), rounded, so the highlight follows the field's corners
// instead of a second square box sitting over a rounded one.
const FIELD_BOX_CLASS = 'flex h-8 w-full items-stretch';
const FIELD_CLASS = 'h-full w-full rounded border-gray-medium bg-white';

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-sm text-gray-dark">{children}</span>
);

// The task's own fields in the category sheet (OP-2556), opened from its Task
// cell so every row of the sheet stays one product: Date, Timing, Area and
// unit. Water rate, Target and Notes are on the grid, and join these only
// while View options hides them there. Each field saves on its own.
const TaskSheetDetailsForm: React.FC<TaskSheetDetailsFormProps> = ({
  task,
  gUnits,
  selectedInputStartDate,
  areaUnitOptions,
  commitTaskField,
  onDateEditEnd,
  showWaterRate,
  showTarget,
  showNotes,
  onAddInputs,
}) => {
  const applicationTask = isApplicationLikeTask(task) ? task : null;
  const isOtherCost = isOtherCostTask(task);

  return (
    <div
      className="grid w-96 grid-cols-[6rem_1fr] items-center gap-x-3 gap-y-2 p-3"
      onKeyDown={(event) => {
        // A new task: Ctrl/⌘+Enter from any field saves it and moves on to
        // the product picker, as Ctrl/⌘+Enter confirms elsewhere in the grid.
        if (
          !onAddInputs ||
          event.key !== 'Enter' ||
          !(event.ctrlKey || event.metaKey)
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        // Blur first, so the field being edited commits before the popover
        // closes (its fields save on blur, and unmounting doesn't blur).
        (document.activeElement as HTMLElement | null)?.blur();
        window.setTimeout(onAddInputs, 0);
      }}
    >
      <FieldLabel>{TASKS_GRID_COLUMN_LABEL_DATE}</FieldLabel>
      <div
        className={FIELD_BOX_CLASS}
        {...{ [TASK_DATE_CELL_ATTR]: task.taskId }}
      >
        <EditableCell
          type="date"
          className={FIELD_CLASS}
          value={task.endDate}
          hasDay={task.endDateHasDay}
          displayValue={formatDate(task.endDate, task.endDateHasDay)}
          fallbackDate={selectedInputStartDate}
          onCommit={(endDate, endDateHasDay) =>
            commitTaskField(task, { endDate, endDateHasDay })
          }
          onEditEnd={() => onDateEditEnd?.(task.taskId)}
        />
      </div>

      {/* An other cost has no timing (OP-2399). */}
      {!isOtherCost && (
        <>
          <FieldLabel>{TASKS_GRID_COLUMN_LABEL_TIMING}</FieldLabel>
          <div className={FIELD_BOX_CLASS}>
            <TimingCell
              task={task}
              commitTaskField={commitTaskField}
              className={FIELD_CLASS}
            />
          </div>
        </>
      )}

      <FieldLabel>{TASKS_GRID_COLUMN_LABEL_AREA}</FieldLabel>
      <div className={FIELD_BOX_CLASS}>
        <NumericInputField
          className="h-full w-full"
          inputClassName={FIELD_CLASS}
          min={0}
          value={task.area}
          displayValue={task.area ?? '-'}
          onCommit={(area) => commitTaskField(task, { area })}
        />
      </div>

      <FieldLabel>{TASKS_GRID_COLUMN_LABEL_AREA_UNIT}</FieldLabel>
      <div className={FIELD_BOX_CLASS}>
        <EditableCell
          type="select"
          className={FIELD_CLASS}
          value={task.areaUnit?.globalId ?? null}
          displayValue={task.areaUnit?.unit || ' '}
          options={areaUnitOptions}
          placeholder={TASKS_GRID_SELECT_UNIT_PLACEHOLDER}
          allowClear
          onCommit={(globalId) => {
            if (globalId === null) {
              commitTaskField(task, { areaUnit: null });
              return;
            }
            const selected = gUnits.find((unit) => unit.globalId === globalId);
            if (!selected) return;
            commitTaskField(task, {
              areaUnit: { unit: selected.code, globalId: selected.globalId },
            });
          }}
        />
      </div>

      {applicationTask && (showWaterRate || onAddInputs) && (
        <>
          <FieldLabel>{TASKS_GRID_WATER_RATE_FIELD_LABEL}</FieldLabel>
          <NumericInputField
            unit={TASKS_GRID_WATER_RATE_UNIT_LABEL}
            inputClassName="h-8 w-full rounded border-gray-medium bg-white"
            min={0}
            allowEmpty
            value={applicationTask.waterRate}
            placeholder={TASKS_GRID_WATER_RATE_PLACEHOLDER}
            onCommit={(waterRate) => commitTaskField(task, { waterRate })}
          />
        </>
      )}

      {applicationTask && (showTarget || onAddInputs) && (
        <>
          <FieldLabel>{TASKS_GRID_TARGET_FIELD_LABEL}</FieldLabel>
          <TargetSpeciesPicker
            value={applicationTask.targetSpecies}
            onCommit={(items) =>
              commitTaskField(task, { targetSpecies: items })
            }
          />
        </>
      )}

      {(showNotes || onAddInputs) && (
        <>
          <span className="self-start pt-1">
            <FieldLabel>{TASKS_GRID_NOTES_FIELD_LABEL}</FieldLabel>
          </span>
          <TextAreaField
            inputClassName="w-full rounded border-gray-medium bg-white"
            rows={3}
            value={task.notes}
            placeholder={TASKS_GRID_NOTES_PLACEHOLDER}
            maxLength={TASK_NOTES_MAX_LENGTH}
            onCommit={(notes) => commitTaskField(task, { notes })}
          />
        </>
      )}
      {onAddInputs && (
        <div className="col-span-2 mt-1 flex justify-end">
          <span {...{ [TASK_ENTRY_ADD_INPUTS_ATTR]: task.taskId }}>
            <Button
              type="button"
              variant="success"
              size="sm"
              className="px-3"
              onClick={onAddInputs}
            >
              <span className="flex items-center gap-2">
                {TASKS_SHEET_ADD_INPUTS_LABEL}
                <ShortcutHint shortcutKey="Enter" variant="light" />
              </span>
            </Button>
          </span>
        </div>
      )}
    </div>
  );
};

export default TaskSheetDetailsForm;
